import { action, makeObservable, observable } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import { MrbModalParams } from "mrb/common/models";
import { MrbSelectStore } from "mrb/components/select";
import { DropdownItemsResult } from "mrb/components/select/models";
import { localizationService } from "common/localization";

class DeviceOnboardingViewStore extends MrbBaseViewStore {
    @observable.ref certifiedModes = [];
    @observable selectedCertifiedModeId = null;
    @observable isWhitelistMode = false;

    constructor(rootStore, { routeStore }) {
        super(rootStore);
        makeObservable(this);
        this.routeStore = routeStore;
        this.certifiedModeInfoModal = new MrbModalParams(null, null);
        this.selectedCertifiedModeId = this.rootStore.premiumSubscriptionStore.certifiedModeId;
        this.isWhitelistMode = this.rootStore.premiumSubscriptionStore.isWhitelistMode;
        this.openCertifiedModeInfoModal = this.openCertifiedModeInfoModal.bind(this);
        this.modeSelectStore = new MrbSelectStore({
            isSearchable: false,
            virtualized: false,
            initFetch: false,
            isClearable: false,
            textKey: "name",
            valueKey: "id",
            actions: {
                onChange: (option) => {
                    if (option) {
                        this.setCertifiedModeSelection(option.value);
                    }
                },
            },
        });
        this.modeSelectStore.originalItems = new DropdownItemsResult([], 1, 0, 0);
    }

    openCertifiedModeInfoModal() {
        this.certifiedModeInfoModal.open();
    }

    @action.bound
    async setCertifiedModeSelection(value) {
        const prevCertifiedModeId = this.selectedCertifiedModeId;
        const prevWhitelistMode = this.isWhitelistMode;

        let certifiedModeId = null;
        let whitelistMode = false;
        if (value === "whitelist") {
            whitelistMode = true;
        } else if (value !== "naki-standard") {
            certifiedModeId = value;
        }

        this.selectedCertifiedModeId = certifiedModeId;
        this.isWhitelistMode = whitelistMode;

        try {
            await this.routeStore.updateCertifiedMode(certifiedModeId, whitelistMode);
            const selectedMode = this.certifiedModes.find((m) => m.id === certifiedModeId);
            this.rootStore.premiumSubscriptionStore.setCertifiedMode(
                certifiedModeId,
                selectedMode ? selectedMode.name : null,
                whitelistMode
            );
            this.rootStore.notificationStore.success(
                localizationService.t("APPLICATION.USER_SETTINGS.EDIT.FIELDS.CERTIFIED_MODE.SUCCESS_MESSAGE")
            );
        } catch (error) {
            this.selectedCertifiedModeId = prevCertifiedModeId;
            this.isWhitelistMode = prevWhitelistMode;
            this._syncModeSelectStore(prevCertifiedModeId, prevWhitelistMode);
            this.rootStore.notificationStore.error(
                localizationService.t("APPLICATION.USER_SETTINGS.EDIT.FIELDS.CERTIFIED_MODE.ERROR_MESSAGE"),
                error
            );
        }
    }

    @action.bound
    async load() {
        try {
            const response = await this.routeStore.getCertifiedModes();
            this.certifiedModes = response.item || response || [];
        } catch (error) {
            this.certifiedModes = [];
        }
        const allOptions = [
            { id: "naki-standard", name: "The Naki Standard" },
            { id: "whitelist", name: "Whitelist Mode" },
            ...this.certifiedModes.map((m) => ({ id: m.id, name: `${m.name}` })),
        ];
        this.modeSelectStore.originalItems = new DropdownItemsResult(allOptions, 1, allOptions.length, allOptions.length);
        this._syncModeSelectStore(this.selectedCertifiedModeId, this.isWhitelistMode);
    }

    _syncModeSelectStore(certifiedModeId, isWhitelistMode) {
        let option;
        if (certifiedModeId) {
            const mode = this.certifiedModes.find((m) => m.id === certifiedModeId);
            option = mode ? { id: mode.id, name: `${mode.name}` } : { id: "naki-standard", name: "The Naki Standard" };
        } else if (isWhitelistMode) {
            option = { id: "whitelist", name: "Whitelist Mode" };
        } else {
            option = { id: "naki-standard", name: "The Naki Standard" };
        }
        this.modeSelectStore.setSelectedItem(option);
    }
}

export default DeviceOnboardingViewStore;
